import { apiHiringFlow } from "@/api/hiring-flow.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook";
import usePermissions from "@/hooks/use-permissions.hook";
import { ApiPermission } from "@/types/role.types";
import { ApiErrorException } from "@/types/api.types";
import { ApiHiringFlowCreate, ApiHiringFlowUpdate } from "@/types/hiring-flow.types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const hiringFlowsKey = ["hiring-flows"] as const;

export function useHiringFlows(enabled = true) {
  const { canIEdit } = usePermissions();
  const canRead =
    canIEdit(ApiPermission.settings_hiring_flows) || canIEdit(ApiPermission.add_employees);
  return useQuery({
    queryKey: hiringFlowsKey,
    enabled: enabled && canRead,
    queryFn: async () => {
      const result = await apiHiringFlow.list();
      if (!result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
}

export function useHiringFlow(id: number) {
  const { canIEdit } = usePermissions();
  const canRead =
    canIEdit(ApiPermission.settings_hiring_flows) || canIEdit(ApiPermission.add_employees);
  return useQuery({
    queryKey: [...hiringFlowsKey, id],
    enabled: canRead && Number.isSafeInteger(id) && id > 0,
    queryFn: async () => {
      const result = await apiHiringFlow.get(id);
      if (!result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
}

export function useHiringFlowCreate() {
  const client = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async (payload: ApiHiringFlowCreate) => {
      const result = await apiHiringFlow.create(payload);
      if (!result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => client.invalidateQueries({ queryKey: hiringFlowsKey }),
    onError: (error: Error) => onError(error),
  });
}

export function useHiringFlowUpdate() {
  const client = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: ApiHiringFlowUpdate }) => {
      const result = await apiHiringFlow.update(id, payload);
      if (!result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (data) => {
      client.setQueryData([...hiringFlowsKey, data.id], data);
      return client.invalidateQueries({ queryKey: hiringFlowsKey });
    },
    onError: (error: Error) => onError(error),
  });
}

export function useHiringFlowArchive() {
  const client = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiHiringFlow.archive(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => client.invalidateQueries({ queryKey: hiringFlowsKey }),
    onError: (error: Error) => onError(error),
  });
}
